import { GetPlannerDetailResponse } from '@/api/type/types';
import { fonts } from '@/theme/fonts';
import { formatDate } from '@/utils/date-time/formatDate';
import { getCountryNameByCode } from '@/utils/name/getCountryName';
import { formatInternationalNumber } from '@/utils/number/formatInternationalNumber';
import styled from 'styled-components';
import BusinessFileList from './BusinessFileList';
import { useTranslation } from 'react-i18next';

interface PlayerDetailProps {
  detail: GetPlannerDetailResponse;
}
const PlannerDetail = ({ detail }: PlayerDetailProps) => {
  const { t } = useTranslation();
  return (
    <Wrapper>
      <Table>
        <tbody>
          <tr>
            <LabelTd>ID</LabelTd>
            <ValueTd>
              <DataWrap>{detail.loginId}</DataWrap>
            </ValueTd>
          </tr>
          <tr>
            <LabelTd>
              {`${t('common.firstName')} / ${t('common.lastName')}`}
            </LabelTd>
            <ValueTd>
              <DataWrap>
                {`${detail.name.firstName} / ${detail.name.lastName}`}
              </DataWrap>
            </ValueTd>
          </tr>
          <tr>
            <LabelTd>{t('common.dateOfBirth')}</LabelTd>
            <ValueTd>
              <DataWrap>
                {formatDate({ dateString: detail.birthDate })}
              </DataWrap>
            </ValueTd>
          </tr>
          <tr>
            <LabelTd>{t('common.gender')}</LabelTd>
            <ValueTd>
              <DataWrap>
                {detail.gender === 'MALE'
                  ? t('common.male')
                  : t('common.female')}
              </DataWrap>
            </ValueTd>
          </tr>
          <tr>
            <LabelTd>{t('common.nationality')}</LabelTd>
            <ValueTd>
              <DataWrap>
                {getCountryNameByCode({
                  contactCountryCode: detail.contact.contactCountryCode,
                  countryCode: detail.contact.countryCode,
                })}
              </DataWrap>
            </ValueTd>
          </tr>
          <tr>
            <LabelTd>{t('common.mobileNumber')}</LabelTd>
            <ValueTd>
              <DataWrap>
                {formatInternationalNumber({
                  countryCode: detail.contact.contactCountryCode,
                  number: detail.contact.contactNumber,
                })}
              </DataWrap>
            </ValueTd>
          </tr>
          <tr>
            <LabelTd>{t('plannerList.phoneNumber')}</LabelTd>
            <ValueTd>
              <DataWrap>
                {formatInternationalNumber({
                  countryCode: detail.contact.contactCountryCode,
                  number: detail.plannerContactNumber,
                })}
              </DataWrap>
            </ValueTd>
          </tr>
          <tr>
            <LabelTd>{t('plannerList.plannerName')}</LabelTd>
            <ValueTd>
              <DataWrap>{detail.plannerName}</DataWrap>
            </ValueTd>
          </tr>
          <tr>
            <LabelTd>{t('common.address')}</LabelTd>
            {detail.name.countryCode === '+82' && (
              <ValueTd>
                <AddressSection>
                  <DataWrap>{detail.address.postalCode}</DataWrap>
                  <DataWrap>{detail.address.address1}</DataWrap>
                  <DataWrap>{detail.address.address2}</DataWrap>
                  <DataWrap>{detail.address.city ?? ''}</DataWrap>
                  <DataWrap>{detail.address.state ?? ''}</DataWrap>
                </AddressSection>
              </ValueTd>
            )}
            {detail.name.countryCode !== '+82' && (
              <ValueTd>
                <AddressSection>
                  <DataWrap>{detail.address.address1}</DataWrap>
                  <DataWrap>{detail.address.address2}</DataWrap>
                  <DataWrap>{detail.address.city ?? ''}</DataWrap>
                  <DataWrap>{detail.address.state ?? ''}</DataWrap>
                  <DataWrap>{detail.address.postalCode ?? ''}</DataWrap>
                </AddressSection>
              </ValueTd>
            )}
          </tr>
          <tr>
            <LabelTd>{t('plannerList.qualificationDocument')}</LabelTd>
            <ValueTd>
              <FileSection>
                <BusinessFileList fileList={detail.qualificationDocuments} />
              </FileSection>
            </ValueTd>
          </tr>
        </tbody>
      </Table>
    </Wrapper>
  );
};

export default PlannerDetail;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  background: #ffffff;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 8px;
`;

const LabelTd = styled.td`
  width: 140px;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
`;

const ValueTd = styled.td`
  padding: 8px 16px;
  font-size: 14px;
  color: #374151;
`;

const DataWrap = styled.div`
  min-height: 32px;
  padding: 6px 12px;
  display: flex;
  align-items: center;
  border-radius: 6px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  color: #374151;
  font-size: 14px;
`;

const AddressSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ButtonSection = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px;
`;

const DeleteButton = styled.button`
  width: 120px;
  height: 36px;
  border-radius: 6px;
  background: #ef4444;
  color: white;
  font-size: 14px;
  font-weight: ${fonts.weight.semiBold};

  &:hover {
    background: #dc2626;
  }
`;

const FileSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
